import clsx from "clsx";
import CheckoutBar from "@app/components/ui/checkout-bar";
import dayjs from "dayjs";
import { ComputerConfig } from "@app/types";
import { DEFAULT_DATE_TIME_FORMAT, VPS_OS, PathnameUrl } from "@app/constants";
import { formatMoneyVN } from "@app/utils/util";
import { useT } from "@app/contexts/preferences.context";
import { IcMinus, IcPlus, IcServer, IcWallet } from "@app/components/icons";
import { btn, btnGhost, btnLg, btnPrimary, input, select, specMain } from "@app/components/ui";

/** Danh sách hệ điều hành của gói: tách config.os giống RentalConfigModal cũ */
export const getVpsOsOptions = (config?: ComputerConfig) => {
  const options: { label: string; value: VPS_OS }[] = [];

  String(config?.os || "")
    .split(",")
    .map((v) => v.trim().toLowerCase())
    .filter(Boolean)
    .forEach((o) => {
      if (o.includes("ubuntu")) options.push({ label: o, value: VPS_OS.UBUNTU });

      if (o.includes("window")) options.push({ label: o, value: VPS_OS.WINDOW_SERVER });
    });

  return options;
};

const FAQ: [string, string][] = [
  [
    "Vì sao VPS ở trạng thái Đang chờ?",
    "VPS đang được hệ thống khởi tạo và cập nhật. Khi xong, trạng thái chuyển sang Đang thuê và tài khoản, mật khẩu, IP hiện trong VPS của tôi.",
  ],
  [
    "Kết nối VPS thế nào?",
    "Dùng Remote Desktop hoặc SSH tới địa chỉ IP Remote/SSH bằng tài khoản và mật khẩu được cấp. PCRender Tunnel giúp bỏ qua NAT và giảm độ trễ.",
  ],
];

interface VpsSummaryProps {
  config?: ComputerConfig;
  /** Tên VPS khách đặt (tuỳ chọn) */
  name: string;
  onName: (v: string) => void;
  discount: number;
  balance: number;
  months: number;
  os: string;
  agree: boolean;
  showErrors: boolean;
  busy: boolean;
  onMonths: (n: number) => void;
  onOs: (os: VPS_OS) => void;
  onAgree: (v: boolean) => void;
  onUnpick: () => void;
  onCreate: () => void;
  /** Mở trang Nạp tiền với số tiền còn thiếu */
  onTopup: (need: number) => void;
}

/** Khung "Thông tin thuê VPS" (thay cho RentalConfigModal cũ). */
function VpsSummary({
  config,
  discount,
  balance,
  months,
  os,
  agree,
  showErrors,
  busy,
  onMonths,
  onOs,
  onAgree,
  name,
  onName,
  onUnpick,
  onCreate,
  onTopup,
}: VpsSummaryProps) {
  const t = useT();
  const monthPrice = config ? config.monthly_rental_price - (config.monthly_rental_price * discount) / 100 : 0;
  const total = monthPrice * months;
  const lack = total - balance;
  const osOptions = getVpsOsOptions(config);
  // tối đa 12 tháng như bản thiết kế
  const setMonths = (n: number) => onMonths(Math.min(12, Math.max(1, Math.floor(n) || 1)));

  return (
    <>
      <aside id="vps-summary" className="sum-box flex flex-col self-stretch overflow-hidden rounded-[14px] border border-line bg-surface shadow-card max-[1100px]:min-h-0">
        {/* khung cao theo nội dung, không cuộn bên trong; cột thẻ bên cạnh giãn theo cho cao bằng nhau */}
        <div className="sum-in">
          <div className="flex items-center justify-between gap-2 border-b border-line px-4 py-2.5">
            <h2 className="text-[15px] font-bold">{t("Thông tin thuê VPS")}</h2>
            {config && (
              <button type="button" className={clsx(btn, btnGhost, "!h-8")} onClick={onUnpick}>
                {t("Bỏ chọn")}
              </button>
            )}
          </div>

          {!config ? (
        <ol className="m-0 flex flex-1 list-none flex-col gap-3.5 px-4 py-3">
          {[
            ["Chọn gói VPS", "Bấm vào một gói ở bên trái."],
            ["Chọn số tháng và hệ điều hành", "Thuê theo tháng, trừ tiền từ số dư."],
            ["Chờ hệ thống cấp VPS", "Thông tin đăng nhập hiện trong VPS của tôi."],
          ].map(([title, desc], i) => (
            <li key={title} className="grid grid-cols-[28px_1fr] items-start gap-2.5">
              <span
                className={clsx(
                  "grid h-7 w-7 place-items-center rounded-full border text-[13px] font-bold",
                  i === 0 ? "border-brand bg-brand text-white" : "border-line bg-surface-2 text-muted"
                )}
              >
                {i + 1}
              </span>
              <div>
                <b className="block font-semibold">{t(title)}</b>
                <span className="text-[13px] text-muted">{t(desc)}</span>
              </div>
            </li>
          ))}
        </ol>
          ) : (
        <>
          <div className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto px-4 py-3">
            <div className="flex items-center gap-2.5 rounded-[10px] bg-surface-2 px-2 py-1.5">
              <span className="grid h-9 w-9 flex-none place-items-center rounded-lg bg-surface text-brand">
                <IcServer className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <h3 className="text-sm font-bold">{config.config_name}</h3>
                <p className="m-0 truncate text-xs text-muted">
                  {specMain(config.cpu)} · {specMain(config.ram)}
                  {config.gpu ? ` · GPU ${specMain(config.gpu)}` : ""}
                </p>
              </div>
            </div>

            <label className="flex flex-col gap-1 text-[12.5px] font-semibold text-muted">
              <span>
                {t("Tên VPS")} <small className="font-normal text-faint">({t("tuỳ chọn")})</small>
              </span>
              <input
                className={input}
                maxLength={32}
                placeholder={t("Ví dụ: Server giả lập")}
                value={name}
                onChange={(e) => onName(e.target.value)}
              />
            </label>

            <div className="flex flex-wrap items-center gap-1.5">
              <span className="whitespace-nowrap text-[12.5px] font-semibold text-muted">{t("Số tháng")}</span>
              <div className="inline-flex items-center overflow-hidden rounded-[10px] border border-line">
                <button type="button" className="grid h-[34px] w-[30px] place-items-center text-muted hover:bg-surface-2" onClick={() => setMonths(months - 1)}>
                  <IcMinus className="h-4 w-4" />
                </button>
                <input
                  type="number"
                  min={1}
                  max={12}
                  value={months}
                  onChange={(e) => setMonths(Number(e.target.value))}
                  className="h-[34px] w-10 border-x border-line bg-transparent text-center font-semibold tabular-nums outline-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <button type="button" className="grid h-[34px] w-[30px] place-items-center text-muted hover:bg-surface-2" onClick={() => setMonths(months + 1)}>
                  <IcPlus className="h-4 w-4" />
                </button>
              </div>
              <div className="ml-auto flex gap-1">
                {[1, 3, 6, 12].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setMonths(n)}
                    className={clsx(
                      "min-w-[30px] rounded-[7px] border px-2 py-[3px] text-xs",
                      months === n ? "border-brand text-brand" : "border-line text-muted"
                    )}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>

            <label className="flex flex-col gap-1 text-[12.5px] font-semibold text-muted">
              {t("Hệ điều hành")}
              <select
                className={clsx(select, "w-full", showErrors && !os && "!border-bad")}
                value={os}
                onChange={(e) => onOs(e.target.value as VPS_OS)}
              >
                <option value="">{t("Chọn hệ điều hành")}</option>
                {osOptions.map((o) => (
                  <option key={o.label} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              {showErrors && !os && <small className="font-medium text-bad">{t("Cần chọn hệ điều hành")}</small>}
            </label>

            <div className="flex flex-col gap-1 rounded-[10px] bg-surface-2 px-3 py-2.5 text-[13px] tabular-nums">
              {(
                [
                  ["Giá mỗi tháng", `${formatMoneyVN(monthPrice)} VND`],
                  ["Thời gian thuê", `${months} ${t("tháng")}`],
                  ["Kết thúc dự kiến", dayjs().add(months, "month").format(DEFAULT_DATE_TIME_FORMAT)],
                ] as [string, string][]
              ).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3">
                  <span className="text-muted">{t(k)}</span>
                  <b>{v}</b>
                </div>
              ))}
            </div>

            <div className="flex flex-col overflow-hidden rounded-[10px] border border-line">
              {FAQ.map(([q, a], i) => (
                <details key={q} className={clsx("group", i > 0 && "border-t border-line")}>
                  <summary className="flex cursor-pointer list-none justify-between gap-2 px-3 py-2 text-[12.5px] font-semibold after:font-bold after:text-faint after:content-['+'] group-open:after:content-['–'] [&::-webkit-details-marker]:hidden">
                    {t(q)}
                  </summary>
                  <p className="m-0 px-3 pb-2.5 text-[12.5px] leading-snug text-muted">{t(a)}</p>
                </details>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2.5 border-t border-line px-4 pb-3.5 pt-2.5">
            <div className="flex items-baseline justify-between rounded-[10px] bg-surface-2 px-3 py-2.5 tabular-nums">
              <span className="font-semibold">{t("Thành tiền")}</span>
              <strong className="text-lg text-brand">{formatMoneyVN(total)} VND</strong>
            </div>
            <label
              className={clsx(
                "flex cursor-pointer items-start gap-2 text-[12.5px] leading-snug",
                showErrors && !agree ? "text-bad" : "text-muted"
              )}
            >
              <input
                type="checkbox"
                className="mt-px h-4 w-4 flex-none accent-[var(--brand)]"
                checked={agree}
                onChange={(e) => onAgree(e.target.checked)}
              />
              <span>
                {t("Tôi hiểu VPS không được hoàn tiền sau khi thuê, đã chọn đúng thời gian sử dụng và đồng ý")}{" "}
                <a className="font-semibold text-brand" href={PathnameUrl.TERMS} target="_blank" rel="noreferrer">
                  {t("điều khoản sử dụng")}
                </a>
                .
              </span>
            </label>
            {lack > 0 ? (
              <button type="button" className={clsx(btn, btnPrimary, btnLg, "w-full")} onClick={() => onTopup(lack)}>
                <IcWallet className="h-[18px] w-[18px]" />
                {t("Nạp thêm")} {formatMoneyVN(lack)} VND
              </button>
            ) : (
              <button type="button" className={clsx(btn, btnPrimary, btnLg, "w-full")} disabled={busy} onClick={onCreate}>
                <IcServer className="h-[18px] w-[18px]" />
                {`${t("Bắt đầu thuê")} · ${formatMoneyVN(total)}đ`}
              </button>
            )}
          </div>
        </>
          )}
        </div>
      </aside>
      <CheckoutBar
        targetId="vps-summary"
        active={!!config}
        name={config ? `${config.config_name} · ${months} ${t("tháng")}` : ""}
        total={`${formatMoneyVN(total)} VND`}
      />
    </>
  );
}

export default VpsSummary;
